import { prepareFastEntryForPersistence, type FastEntry } from '../utils/fastEntry'

export type RemoteHistorySummary = {
   period: string | null
   totalGrams: number
   fastCount: number
}

type HistoryApiDeps = {
   historyEndpoint: string
   historySummaryEndpoint: string
   canUseRemoteSync: () => boolean
   getUserId: () => string
   resolveSyncUserId: () => Promise<string>
}

/**
 * Extracts an `error` message from a JSON error response, falling back to a default.
 *
 * @param response - The error response.
 * @param fallback - The message to use when none is present.
 * @returns The error message.
 */
async function readErrorMessage(response: Response, fallback: string) {
   try {
      const errorPayload: unknown = await response.json()
      if (
         errorPayload &&
         typeof errorPayload === 'object' &&
         typeof (errorPayload as Record<string, unknown>).error === 'string'
      ) {
         return (errorPayload as Record<string, unknown>).error as string
      }
   } catch {
      // ignore parse errors
   }
   return fallback
}

/**
 * Parses a response body as a JSON object, or null when not an object.
 *
 * @param response - The fetch response.
 * @returns The parsed record, or null.
 */
async function parseJsonRecord(
   response: Response,
): Promise<Record<string, unknown> | null> {
   const rawData: unknown = await response.json()
   return rawData && typeof rawData === 'object' ?
         (rawData as Record<string, unknown>)
      :  null
}

/**
 * Resolves the hashed user id when remote history calls are allowed.
 *
 * @param deps - The API dependencies.
 * @returns The hashed id, or null when remote sync is unavailable.
 */
async function resolveRemoteUserId(deps: HistoryApiDeps) {
   if (!deps.getUserId() || !deps.canUseRemoteSync()) {
      return null
   }
   return (await deps.resolveSyncUserId()) || null
}

/**
 * Sends a JSON request to the history endpoint and parses the JSON reply,
 * throwing with the server's error message on a non-2xx status.
 *
 * @param deps - The API dependencies.
 * @param method - The HTTP method.
 * @param body - The JSON body.
 * @param failureLabel - Prefix for the thrown error when the server gives no message.
 * @returns The parsed response record, or null.
 */
async function sendHistoryRequest(
   deps: HistoryApiDeps,
   method: 'POST' | 'DELETE',
   body: unknown,
   failureLabel: string,
) {
   const response = await fetch(deps.historyEndpoint, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
   })
   if (!response.ok) {
      throw new Error(
         await readErrorMessage(response, `${failureLabel} failed (${response.status})`),
      )
   }
   return parseJsonRecord(response)
}

/**
 * Creates or updates a fast-history entry on the server, storing the returned id back on the entry.
 *
 * @param deps - The API dependencies.
 * @param entry - The fast entry to upsert.
 * @returns The resulting history id, or null when skipped.
 */
async function upsertRemoteHistoryEntry(deps: HistoryApiDeps, entry: FastEntry) {
   if (!entry || typeof entry !== 'object') {
      return null
   }
   const hashedId = await resolveRemoteUserId(deps)
   if (!hashedId) {
      return null
   }
   const payloadEntry = prepareFastEntryForPersistence(entry)
   delete payloadEntry.historyId
   const payload: { userId: string; entry: Record<string, unknown>; historyId?: number } =
      {
         userId: hashedId,
         entry: payloadEntry,
      }
   if (entry.historyId) {
      payload.historyId = entry.historyId
   }
   try {
      const data = await sendHistoryRequest(deps, 'POST', payload, 'History upsert')
      if (typeof data?.historyId === 'number' && Number.isInteger(data.historyId)) {
         entry.historyId = data.historyId
      }
      return entry.historyId || null
   } catch (error) {
      console.error('Failed to upsert remote history entry', error)
      throw error
   }
}

/**
 * Deletes a fast-history entry on the server by id.
 *
 * @param deps - The API dependencies.
 * @param historyId - The server history id.
 * @returns True when deleted (or already gone), false when skipped.
 */
async function deleteRemoteHistoryEntry(deps: HistoryApiDeps, historyId: number) {
   if (!Number.isInteger(historyId) || historyId <= 0) {
      return false
   }
   const hashedId = await resolveRemoteUserId(deps)
   if (!hashedId) {
      return false
   }
   try {
      const data = await sendHistoryRequest(
         deps,
         'DELETE',
         { userId: hashedId, historyId },
         'History delete',
      )
      return data?.ok !== false
   } catch (error) {
      console.error('Failed to delete remote history entry', error)
      throw error
   }
}

/**
 * Fetches the aggregated fat-burn history summary for a period.
 *
 * @param deps - The API dependencies.
 * @param period - The range key (e.g. `week`/`month`).
 * @returns The summary, or null when unavailable.
 */
async function fetchRemoteHistorySummary(
   deps: HistoryApiDeps,
   period: string,
): Promise<RemoteHistorySummary | null> {
   const hashedId = await resolveRemoteUserId(deps)
   if (!hashedId) {
      return null
   }
   const params = new URLSearchParams({ userId: hashedId, period })
   const response = await fetch(`${deps.historySummaryEndpoint}?${params.toString()}`, {
      method: 'GET',
   })
   if (!response.ok) {
      console.error(
         'Failed to fetch remote history summary',
         response.status,
         response.statusText,
      )
      return null
   }
   const data = await parseJsonRecord(response)
   if (data?.ok !== true) {
      return null
   }
   return {
      period: typeof data.period === 'string' ? data.period : null,
      totalGrams: typeof data.totalGrams === 'number' ? data.totalGrams : 0,
      fastCount: typeof data.fastCount === 'number' ? data.fastCount : 0,
   }
}

/**
 * Creates the remote fast-history API client (upsert, delete, and summary endpoints).
 *
 * @param deps - Injected endpoints and user/sync accessors.
 * @returns The history API methods.
 */
export function createHistoryApi(deps: HistoryApiDeps) {
   return {
      upsertRemoteHistoryEntry: (entry: FastEntry) =>
         upsertRemoteHistoryEntry(deps, entry),
      deleteRemoteHistoryEntry: (historyId: number) =>
         deleteRemoteHistoryEntry(deps, historyId),
      fetchRemoteHistorySummary: (period: string) =>
         fetchRemoteHistorySummary(deps, period),
   }
}
